import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../api/axios";
import toast from "react-hot-toast";
import QuillEditor from "../common/QuillEditor";
import { Save, ArrowLeft, Loader2, ImagePlus, Plus, Trash2, Edit, X, HelpCircle, ChevronDown, ChevronUp, GripVertical, Globe, Search } from "lucide-react";

export default function AddBlogPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [activeLang, setActiveLang] = useState(null);
  const [slugTouched, setSlugTouched] = useState(false);

  // Blog FAQ state (client-side yönetim)
  const [faqs, setFaqs] = useState([]);
  const [editingFaqIndex, setEditingFaqIndex] = useState(null);
  const [faqFormOpen, setFaqFormOpen] = useState(false);
  const [faqModalOpen, setFaqModalOpen] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [faqForm, setFaqForm] = useState({
    questionTr: "", questionEn: "", questionAr: "",
    answerTr: "", answerEn: "", answerAr: "",
    sortOrder: 1,
  });

  const [formData, setFormData] = useState({
    categoryId: "", titleTr: "", titleEn: "", titleAr: "",
    descriptionTr: "", descriptionEn: "", descriptionAr: "",
    image: null, imagePreview: "", tags: "", isActive: true,
    readingTime: 0, seoTitle: "", metaDescription: "", slug: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, blogRes] = await Promise.all([
          api.get("/setting"),
          id ? api.get(`/blog/${id}`) : Promise.resolve(null),
        ]);
        setSettings(settingsRes.data);
        const s = settingsRes.data;
        if (s.turkish) setActiveLang("tr");
        else if (s.english) setActiveLang("en");
        else if (s.arabic) setActiveLang("ar");

        if (settingsRes.data.isBlogCategoryOn) {
          const catRes = await api.get("/categories");
          setCategories(catRes.data || []);
        }

        if (blogRes) {
          const d = blogRes.data;
          setFormData((p) => ({
            ...p,
            categoryId: d.categoryId || "", titleTr: d.titleTr || "", titleEn: d.titleEn || "", titleAr: d.titleAr || "",
            descriptionTr: d.descriptionTr || "", descriptionEn: d.descriptionEn || "", descriptionAr: d.descriptionAr || "",
            imagePreview: d.imageUrl || "", tags: d.tags || "", readingTime: d.readingTime || 0,
            isActive: d.isActive !== undefined ? d.isActive : true,
            seoTitle: d.seoTitle || "", metaDescription: d.metaDescription || "", slug: d.slug || "",
          }));
          setFaqs(d.blogFAQs || d.blogFaqs || []);
          if (d.slug) setSlugTouched(true);
        }
      } catch { toast.error("Veriler yüklenemedi"); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [id]);

  const generateSlug = (title) =>
    title.toLowerCase()
      .replace(/ğ/g, "g").replace(/ü/g, "u").replace(/ş/g, "s")
      .replace(/ı/g, "i").replace(/ö/g, "o").replace(/ç/g, "c")
      .replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-").replace(/^-+|-+$/g, "");

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    const newData = { ...formData, [name]: type === "file" ? files[0] : value };

    if (type === "file" && files[0]) {
      const reader = new FileReader();
      reader.onload = (ev) => setFormData((p) => ({ ...p, imagePreview: ev.target.result }));
      reader.readAsDataURL(files[0]);
    }

    if (name === "slug") {
      setSlugTouched(true);
    }

    if ((name === "titleTr" || name === "titleEn") && !slugTouched) newData.slug = generateSlug(value);
    if ((name === "titleTr" || name === "titleEn") && !formData.seoTitle) newData.seoTitle = value;
    setFormData(newData);
  };

  const validate = () => {
    const errs = {};
    if (!formData.metaDescription.trim())
      errs.metaDescription = "Meta açıklama zorunludur";

    if (settings?.turkish && !formData.titleTr.trim())
      errs.titleTr = "Türkçe başlık zorunludur";
    if (settings?.english && !formData.titleEn.trim())
      errs.titleEn = "İngilizce başlık zorunludur";
    if (settings?.arabic && !formData.titleAr.trim())
      errs.titleAr = "Arapça başlık zorunludur";

    if (!formData.slug.trim())
      errs.slug = "URL (Slug) zorunludur";

    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      toast.error("Lütfen zorunlu alanları doldurun");
      return;
    }
    setErrors({});
    setSaving(true);

    const data = new FormData();
    data.append("CategoryId", formData.categoryId);
    data.append("TitleTr", formData.titleTr);
    data.append("TitleEn", formData.titleEn);
    data.append("TitleAr", formData.titleAr);
    data.append("DescriptionTr", formData.descriptionTr);
    data.append("DescriptionEn", formData.descriptionEn);
    data.append("DescriptionAr", formData.descriptionAr);
    data.append("Tags", formData.tags);
    data.append("IsActive", formData.isActive);
    data.append("ReadingTime", formData.readingTime);
    data.append("SeoTitle", formData.seoTitle);
    data.append("MetaDescription", formData.metaDescription);
    data.append("Slug", formData.slug);
    if (formData.image) data.append("file", formData.image);

    // FAQ'ları JSON string olarak gönder (boş dizi de gönderilmeli ki backend mevcut FAQ'ları silsin)
    data.append("blogFAQs", JSON.stringify(
      faqs.map((faq, i) => ({
        questionTr: faq.questionTr || "",
        questionEn: faq.questionEn || "",
        questionAr: faq.questionAr || "",
        answerTr: faq.answerTr || "",
        answerEn: faq.answerEn || "",
        answerAr: faq.answerAr || "",
        sortOrder: faq.sortOrder || i + 1,
      }))
    ));

    try {
      if (id) {
        await api.put(`/blog/${id}`, data, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Blog güncellendi");
      } else {
        await api.post("/blog", data, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Blog eklendi");
      }
      navigate("/blog-list");
    } catch { toast.error("Blog kaydedilirken hata oluştu"); }
    finally { setSaving(false); }
  };

  const resetFaqForm = () => {
    setFaqForm({ questionTr: "", questionEn: "", questionAr: "", answerTr: "", answerEn: "", answerAr: "", sortOrder: faqs.length + 1 });
    setEditingFaqIndex(null);
    setFaqFormOpen(false);
  };

  const handleFaqChange = (e) => {
    setFaqForm({ ...faqForm, [e.target.name]: e.target.value });
  };

  const handleFaqAdd = () => {
    if (!faqForm.questionTr && !faqForm.questionEn && !faqForm.questionAr) {
      return toast.error("En az bir dilde soru giriniz");
    }
    if (editingFaqIndex !== null) {
      setFaqs((prev) => prev.map((f, i) => i === editingFaqIndex ? { ...faqForm } : f));
      toast.success("SSS güncellendi");
    } else {
      setFaqs((prev) => [...prev, { ...faqForm, sortOrder: faqForm.sortOrder || prev.length + 1 }]);
      toast.success("SSS eklendi");
    }
    resetFaqForm();
  };

  const moveFaq = (index, direction) => {
    const newFaqs = [...faqs];
    const target = index + direction;
    if (target < 0 || target >= newFaqs.length) return;
    [newFaqs[index], newFaqs[target]] = [newFaqs[target], newFaqs[index]];
    newFaqs.forEach((f, i) => (f.sortOrder = i + 1));
    setFaqs(newFaqs);
  };

  const handleFaqDelete = (index) => {
    if (!window.confirm("Bu SSS'yi silmek istediğinize emin misiniz?")) return;
    setFaqs((prev) => prev.filter((_, i) => i !== index));
    if (editingFaqIndex === index) resetFaqForm();
  };

  const startEditFaq = (index) => {
    const faq = faqs[index];
    setEditingFaqIndex(index);
    setFaqFormOpen(true);
    setFaqForm({
      questionTr: faq.questionTr || "", questionEn: faq.questionEn || "", questionAr: faq.questionAr || "",
      answerTr: faq.answerTr || "", answerEn: faq.answerEn || "", answerAr: faq.answerAr || "",
      sortOrder: faq.sortOrder || 1,
    });
  };

  if (loading) return <div className="page-loading"><div className="loading-spinner" /><p>Yükleniyor...</p></div>;
  if (!settings) return <div className="page-loading"><p>Ayarlar bulunamadı.</p></div>;

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h2>{id ? "Blog Düzenle" : "Yeni Blog Ekle"}</h2>
          <p className="page-subtitle">{id ? "Blog yazısını düzenleyin" : "Yeni bir blog yazısı oluşturun"}</p>
        </div>
        <button className="btn btn-ghost" onClick={() => navigate("/blog-list")}>
          <ArrowLeft size={18} /> Listeye Dön
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-layout-2col">
          <div className="form-main">
            {settings.isBlogCategoryOn && (
              <div className="content-card">
                <div className="form-field">
                  <label>Kategori</label>
                  <select name="categoryId" value={formData.categoryId} onChange={handleChange} className="form-input">
                    <option value="">Kategori Seçiniz</option>
                    {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              </div>
            )}

            <div className="content-card">
              <div className="lang-tabs">
                {settings.turkish && (
                  <button type="button" className={`lang-tab${activeLang === "tr" ? " active" : ""}${errors.titleTr ? " has-error" : ""}`} onClick={() => setActiveLang("tr")}>
                    🇹🇷 Türkçe
                  </button>
                )}
                {settings.english && (
                  <button type="button" className={`lang-tab${activeLang === "en" ? " active" : ""}${errors.titleEn ? " has-error" : ""}`} onClick={() => setActiveLang("en")}>
                    🇬🇧 English
                  </button>
                )}
                {settings.arabic && (
                  <button type="button" className={`lang-tab${activeLang === "ar" ? " active" : ""}${errors.titleAr ? " has-error" : ""}`} onClick={() => setActiveLang("ar")}>
                    🇸🇦 العربية
                  </button>
                )}
              </div>

              {activeLang === "tr" && settings.turkish && (
                <div className="lang-tab-content">
                  <div className="form-field">
                    <label>Başlık <span className="required">*</span></label>
                    <input type="text" name="titleTr" value={formData.titleTr} onChange={handleChange} className={`form-input${errors.titleTr ? " input-error" : ""}`} placeholder="Türkçe başlık" />
                    {errors.titleTr && <span className="field-error">{errors.titleTr}</span>}
                  </div>
                  <div className="form-field">
                    <label>İçerik</label>
                    <QuillEditor value={formData.descriptionTr} onChange={(c) => setFormData((p) => ({ ...p, descriptionTr: c }))} placeholder="Blog içeriği..." />
                  </div>
                </div>
              )}

              {activeLang === "en" && settings.english && (
                <div className="lang-tab-content">
                  <div className="form-field">
                    <label>Title <span className="required">*</span></label>
                    <input type="text" name="titleEn" value={formData.titleEn} onChange={handleChange} className={`form-input${errors.titleEn ? " input-error" : ""}`} placeholder="English title" />
                    {errors.titleEn && <span className="field-error">{errors.titleEn}</span>}
                  </div>
                  <div className="form-field">
                    <label>Content</label>
                    <QuillEditor value={formData.descriptionEn} onChange={(c) => setFormData((p) => ({ ...p, descriptionEn: c }))} placeholder="Blog content..." />
                  </div>
                </div>
              )}

              {activeLang === "ar" && settings.arabic && (
                <div className="lang-tab-content">
                  <div className="form-field">
                    <label>العنوان <span className="required">*</span></label>
                    <input type="text" name="titleAr" value={formData.titleAr} onChange={handleChange} className={`form-input${errors.titleAr ? " input-error" : ""}`} placeholder="العنوان" />
                    {errors.titleAr && <span className="field-error">{errors.titleAr}</span>}
                  </div>
                  <div className="form-field">
                    <label>المحتوى</label>
                    <QuillEditor value={formData.descriptionAr} onChange={(c) => setFormData((p) => ({ ...p, descriptionAr: c }))} placeholder="المحتوى" dir="rtl" />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="form-sidebar">
            <div className="content-card sticky-card">
              <h3 className="card-title">Kapak Görseli</h3>
              <div className="image-upload-area">
                {formData.imagePreview ? (
                  <div className="image-preview">
                    <img src={formData.imagePreview} alt="Preview" />
                    <button type="button" className="remove-image" onClick={() => setFormData((p) => ({ ...p, image: null, imagePreview: "" }))}>×</button>
                  </div>
                ) : (
                  <label className="upload-placeholder">
                    <ImagePlus size={32} />
                    <span>Görsel Yükle</span>
                    <input type="file" name="image" accept="image/*" onChange={handleChange} hidden />
                  </label>
                )}
              </div>
            </div>

            <div className="content-card">
              <h3 className="card-title">Blog Ayarları</h3>
              <div className="form-field">
                <label>Etiketler</label>
                <input type="text" name="tags" value={formData.tags} onChange={handleChange} className="form-input" placeholder="react, web, tasarım" />
              </div>
              <div className="form-field">
                <label>Okuma Süresi (dk)</label>
                <input type="number" name="readingTime" value={formData.readingTime} onChange={handleChange} className="form-input" min={0} />
              </div>
              <div className="form-field">
                <label className="toggle-label">
                  <span>Aktif</span>
                  <label className="toggle">
                    <input type="checkbox" name="isActive" checked={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} />
                    <span className="toggle-slider" />
                  </label>
                </label>
              </div>
            </div>

            <div className="content-card">
              <h3 className="card-title"><Search size={16} /> SEO Ayarları</h3>
              <div className="form-field">
                <label>URL (Slug) <span className="required">*</span></label>
                <div className="slug-input-wrapper">
                  <span className="slug-prefix">/blog/</span>
                  <input type="text" name="slug" value={formData.slug} onChange={handleChange} className={`form-input${errors.slug ? " input-error" : ""}`} placeholder="blog-basligi" />
                </div>
                {errors.slug && <span className="field-error">{errors.slug}</span>}
              </div>
              <div className="form-field">
                <label>SEO Başlık <small className="char-count">{formData.seoTitle.length}/60</small></label>
                <input type="text" name="seoTitle" value={formData.seoTitle} onChange={handleChange} className="form-input" maxLength={60} />
              </div>
              <div className="form-field">
                <label>
                  Meta Açıklama <span className="required">*</span>{" "}
                  <small className="char-count">{formData.metaDescription.length}/160</small>
                </label>
                <textarea name="metaDescription" value={formData.metaDescription} onChange={handleChange} className={`form-input${errors.metaDescription ? " input-error" : ""}`} rows={3} maxLength={160} placeholder="Meta açıklama giriniz..." />
                {errors.metaDescription && <span className="field-error">{errors.metaDescription}</span>}
              </div>
              {(formData.seoTitle || formData.slug) && (
                <div className="seo-preview">
                  <div className="seo-preview-label">Google Önizleme</div>
                  <div className="seo-preview-title">{formData.seoTitle || formData.titleTr || formData.titleEn || "Sayfa Başlığı"}</div>
                  <div className="seo-preview-url">softana.com.tr/blog/{formData.slug || "..."}</div>
                  <div className="seo-preview-desc">{formData.metaDescription || "Meta açıklama burada görünecek..."}</div>
                </div>
              )}
            </div>

            <div className="content-card faq-trigger-card">
              <button type="button" className="btn btn-outline btn-full" onClick={() => setFaqModalOpen(true)}>
                <HelpCircle size={18} />
                Sıkça Sorulan Sorular
                {faqs.length > 0 && <span className="faq-badge">{faqs.length}</span>}
              </button>
            </div>

            <button type="submit" className="btn btn-primary btn-full" disabled={saving}>
              {saving ? <><Loader2 size={18} className="spin" /> Kaydediliyor...</> : <><Save size={18} /> {id ? "Güncelle" : "Kaydet"}</>}
            </button>
          </div>
        </div>
      </form>

      {faqModalOpen && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setFaqModalOpen(false); }}>
          <div className="modal-container modal-lg">
            <div className="modal-header">
              <div className="modal-header-left">
                <HelpCircle size={20} />
                <div>
                  <h3>Sıkça Sorulan Sorular</h3>
                  <span className="modal-subtitle">{faqs.length} soru eklendi</span>
                </div>
              </div>
              <div className="modal-header-actions">
                {!faqFormOpen && (
                  <button type="button" className="btn btn-primary btn-sm" onClick={() => { resetFaqForm(); setFaqFormOpen(true); setFaqForm(f => ({ ...f, sortOrder: faqs.length + 1 })); }}>
                    <Plus size={16} /> Yeni SSS
                  </button>
                )}
                <button type="button" className="btn-icon" onClick={() => setFaqModalOpen(false)}>
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className="modal-body">
              {faqs.length > 0 && (
                <div className="faq-list">
                  {faqs.map((faq, index) => (
                    <div key={index} className={`faq-card${expandedFaq === index ? " expanded" : ""}${editingFaqIndex === index ? " editing" : ""}`}>
                      <div className="faq-card-header" onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}>
                        <div className="faq-card-left">
                          <span className="faq-number">{index + 1}</span>
                          <div className="faq-card-title">
                            <span className="faq-question">{faq.questionTr || faq.questionEn || faq.questionAr || "Soru girilmemiş"}</span>
                            {faq.questionEn && faq.questionTr && <span className="faq-question-sub">{faq.questionEn}</span>}
                          </div>
                        </div>
                        <div className="faq-card-actions">
                          <div className="faq-reorder">
                            <button type="button" className="btn-icon small" onClick={(e) => { e.stopPropagation(); moveFaq(index, -1); }} disabled={index === 0} title="Yukarı taşı">
                              <ChevronUp size={14} />
                            </button>
                            <button type="button" className="btn-icon small" onClick={(e) => { e.stopPropagation(); moveFaq(index, 1); }} disabled={index === faqs.length - 1} title="Aşağı taşı">
                              <ChevronDown size={14} />
                            </button>
                          </div>
                          <button type="button" className="btn-icon edit" onClick={(e) => { e.stopPropagation(); startEditFaq(index); }} title="Düzenle">
                            <Edit size={15} />
                          </button>
                          <button type="button" className="btn-icon delete" onClick={(e) => { e.stopPropagation(); handleFaqDelete(index); }} title="Sil">
                            <Trash2 size={15} />
                          </button>
                          {expandedFaq === index ? <ChevronUp size={18} className="faq-chevron" /> : <ChevronDown size={18} className="faq-chevron" />}
                        </div>
                      </div>
                      {expandedFaq === index && (
                        <div className="faq-card-body">
                          {settings.turkish && (faq.answerTr || faq.questionTr) && (
                            <div className="faq-answer-block">
                              <span className="faq-lang-badge">TR</span>
                              <div>
                                <div className="faq-answer-q">{faq.questionTr}</div>
                                <div className="faq-answer-a">{faq.answerTr || <em style={{ opacity: 0.5 }}>Cevap girilmemiş</em>}</div>
                              </div>
                            </div>
                          )}
                          {settings.english && (faq.answerEn || faq.questionEn) && (
                            <div className="faq-answer-block">
                              <span className="faq-lang-badge">EN</span>
                              <div>
                                <div className="faq-answer-q">{faq.questionEn}</div>
                                <div className="faq-answer-a">{faq.answerEn || <em style={{ opacity: 0.5 }}>No answer</em>}</div>
                              </div>
                            </div>
                          )}
                          {settings.arabic && (faq.answerAr || faq.questionAr) && (
                            <div className="faq-answer-block" dir="rtl">
                              <span className="faq-lang-badge">AR</span>
                              <div>
                                <div className="faq-answer-q">{faq.questionAr}</div>
                                <div className="faq-answer-a">{faq.answerAr || <em style={{ opacity: 0.5 }}>لا جواب</em>}</div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {faqs.length === 0 && !faqFormOpen && (
                <div className="faq-empty">
                  <HelpCircle size={40} strokeWidth={1.2} />
                  <p>Henüz soru eklenmemiş</p>
                  <button type="button" className="btn btn-primary btn-sm" onClick={() => setFaqFormOpen(true)}>
                    <Plus size={16} /> İlk Soruyu Ekle
                  </button>
                </div>
              )}

              {faqFormOpen && (
                <div className="faq-form-panel">
                  <div className="faq-form-header">
                    <h4>{editingFaqIndex !== null ? "SSS Düzenle" : "Yeni SSS Ekle"}</h4>
                    <button type="button" className="btn-icon" onClick={resetFaqForm}>
                      <X size={18} />
                    </button>
                  </div>

                  <div className="faq-form-body">
                    {settings.turkish && (
                      <div className="faq-lang-group">
                        <div className="faq-lang-label"><span className="faq-lang-badge">TR</span> Türkçe</div>
                        <div className="faq-form-row">
                          <div className="form-field">
                            <label>Soru</label>
                            <input type="text" name="questionTr" value={faqForm.questionTr} onChange={handleFaqChange} className="form-input" placeholder="Soru giriniz" />
                          </div>
                          <div className="form-field">
                            <label>Cevap</label>
                            <textarea name="answerTr" value={faqForm.answerTr} onChange={handleFaqChange} className="form-input" rows={2} placeholder="Cevap giriniz" />
                          </div>
                        </div>
                      </div>
                    )}

                    {settings.english && (
                      <div className="faq-lang-group">
                        <div className="faq-lang-label"><span className="faq-lang-badge">EN</span> English</div>
                        <div className="faq-form-row">
                          <div className="form-field">
                            <label>Question</label>
                            <input type="text" name="questionEn" value={faqForm.questionEn} onChange={handleFaqChange} className="form-input" placeholder="Enter question" />
                          </div>
                          <div className="form-field">
                            <label>Answer</label>
                            <textarea name="answerEn" value={faqForm.answerEn} onChange={handleFaqChange} className="form-input" rows={2} placeholder="Enter answer" />
                          </div>
                        </div>
                      </div>
                    )}

                    {settings.arabic && (
                      <div className="faq-lang-group">
                        <div className="faq-lang-label"><span className="faq-lang-badge">AR</span> العربية</div>
                        <div className="faq-form-row">
                          <div className="form-field">
                            <label>السؤال</label>
                            <input type="text" name="questionAr" value={faqForm.questionAr} onChange={handleFaqChange} className="form-input" placeholder="أدخل السؤال" dir="rtl" />
                          </div>
                          <div className="form-field">
                            <label>الجواب</label>
                            <textarea name="answerAr" value={faqForm.answerAr} onChange={handleFaqChange} className="form-input" rows={2} placeholder="أدخل الجواب" dir="rtl" />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="faq-form-footer">
                    <button type="button" className="btn btn-ghost" onClick={resetFaqForm}>
                      İptal
                    </button>
                    <button type="button" className="btn btn-primary" onClick={handleFaqAdd}>
                      {editingFaqIndex !== null ? <><Save size={16} /> Güncelle</> : <><Plus size={16} /> Ekle</>}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-primary" onClick={() => { resetFaqForm(); setFaqModalOpen(false); }}>
                Tamam
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
